import React from 'react';
import {
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
} from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { RiskConfigCard } from '@innovyze/lib-am-common';
import { AssetFilterListItem } from '@Types/asset.types';
import { RiskDetailsForm } from './useRiskDetailsForm';

interface Props {
	form: RiskDetailsForm;
	duplicate?: RiskConfigCard;
	isSaveFilterOn: boolean;
	assetFilters: AssetFilterListItem[];
	disabled?: boolean;
}

export const RiskDetailsStep = ({
	form,
	duplicate,
	isSaveFilterOn,
	assetFilters,
	disabled = false,
}: Props) => {
	const { t } = useGlobalization();
	return (
		<Stack style={{ width: '400px' }}>
			<FormControl variant="outlined" margin="dense" fullWidth>
				<TextField
					fullWidth
					disabled={disabled}
					error={form.configNameError.length !== 0}
					name="name"
					label={t('Enter Name')}
					variant="outlined"
					value={form.newName}
					onChange={form.onNameChange}
					helperText={
						form.configNameError.length === 0
							? ' '
							: form.configNameError
					}
				/>
			</FormControl>
			<FormControl variant="outlined" margin="dense" fullWidth>
				<InputLabel error={form.systemType.length === 0}>
					{t('System Type')}
				</InputLabel>
				<Select
					name="systemType"
					value={form.systemType}
					error={form.systemType.length === 0}
					disabled={disabled || !!duplicate}
					onChange={form.onSystemTypeChange}
					label={t('System Type')}>
					{form.systemTypes.map(system => (
						<MenuItem value={system.id} key={system.id}>
							{t(system.displayName)}
						</MenuItem>
					))}
				</Select>
				<FormHelperText error>
					{form.systemType.length === 0
						? t('Select a System Type')
						: ' '}
				</FormHelperText>
			</FormControl>
			<FormControl variant="outlined" margin="dense" fullWidth>
				<InputLabel
					error={
						form.systemType.length !== 0 &&
						form.assetType.length === 0
					}>
					{t('Asset Type')}
				</InputLabel>
				<Select
					disabled={
						disabled || !!duplicate || form.systemType.length === 0
					}
					data-cy="assetTypeField"
					name="assetType"
					value={form.assetType}
					error={
						form.systemType.length !== 0 &&
						form.assetType.length === 0
					}
					onChange={form.onAssetTypeChange}
					label={t('Asset Type')}>
					{(form.assetTypes ?? []).map(at => (
						<MenuItem value={at.id} key={at.id}>
							{t(at.displayName)}
						</MenuItem>
					))}
				</Select>
				<FormHelperText error>
					{form.systemType.length !== 0 && form.assetType.length === 0
						? t('Select an asset type')
						: ' '}
				</FormHelperText>
			</FormControl>
			{isSaveFilterOn && (
				<FormControl variant="outlined" margin="dense" fullWidth>
					<InputLabel>{t('Asset Filter')}</InputLabel>
					<Select
						name="assetFilter"
						disabled={disabled}
						onChange={form.onAssetFilterChange}
						value={form.assetFilterId}
						label={t('Asset Filter')}>
						{[
							<MenuItem value={''} key={''}>
								{t('(none)')}
							</MenuItem>,
						].concat(
							assetFilters
								.filter(f => f.assetType === form.assetType)
								.map(f => (
									<MenuItem value={f._id} key={f._id}>
										{t(f.name)}
									</MenuItem>
								)),
						)}
					</Select>
					<FormHelperText> </FormHelperText>
				</FormControl>
			)}
			<FormControl variant="outlined" margin="dense" fullWidth>
				<InputLabel
					error={
						!form.disableStandardTypes &&
						form.inspectionStandard.length === 0
					}>
					{t('Inspection Standard')}
				</InputLabel>
				<Select
					disabled={
						disabled ||
						!!duplicate ||
						form.disableStandardTypes ||
						form.assetType.length === 0
					}
					name="inspectionStandard"
					value={form.inspectionStandard}
					error={
						!form.disableStandardTypes &&
						form.inspectionStandard.length === 0
					}
					onChange={form.onInspectionStandardChange}
					label={t('Inspection Standard')}>
					{form.inspectionStandardOptions}
				</Select>
				<FormHelperText error>
					{!form.disableStandardTypes &&
					form.inspectionStandard.length === 0
						? t('Select an Inspection Standard')
						: ' '}
				</FormHelperText>
			</FormControl>
		</Stack>
	);
};
